import { useCallback, useEffect } from 'react';
import ReimbursementWorklistApi from 'api/patient/ReimbursementWorklistApi';
import Loader from 'common/elements/Loader/Loader';
import { differenceBy } from 'lodash/array';
import { debounce } from 'lodash/function';
import { isEmpty } from 'lodash/lang';

import ApplyAndResetButtons from 'components/ApplyAndResetButtons/ApplyAndResetButtons';
import BasicMultiselect from 'components/eui/EuiFiltersContext/controls/BasicMultiselect/BasicMultiselect';
import StudySiteMultiselect from 'components/eui/EuiFiltersContext/controls/StudySiteMultiselect/StudySiteMultiselect';
import { useEuiFiltersDispatch, useEuiFiltersState } from 'components/eui/EuiFiltersContext/EuiFiltersContext';
import { setCacheProcessor, setInitialStateProcessor } from 'components/eui/EuiFiltersContext/processors';
import { FRS_LOADED, FRS_READY } from 'components/eui/EuiFiltersContext/reducer';
import { PageInfoHeader } from 'components/PageInfoHeader/PageInfoHeader';

import {
  RBS_PENDING,
  reimbursementStatuses
} from '../../../patient-source/Patients/PatientInfo/EncountersSection/PaymentsSection/reimbursementConstants';

import Assigned from './Assigned/Assigned';

const debounceCache = debounce(cb => cb(), 500);

export default function FiltersSection({ gridData, applyButton, loadingAllowed }) {
  const state = useEuiFiltersState();
  const dispatch = useEuiFiltersDispatch();

  const { schema } = state;

  const status = state.schema?.status;

  const getStudySites = useCallback(function() {
    return ReimbursementWorklistApi.getStudySitesWithReimbursement().then(({ data: studySites }) => ({ studySites }));
  }, []);

  useEffect(
    function() {
      if (status !== FRS_READY) {
        return;
      }
      debounceCache(function() {
        dispatch({
          type: 'PERFORM',
          processor: setCacheProcessor
        });
      });
    },
    [dispatch, gridData, status]
  );

  useEffect(
    function() {
      if (status) return;
      dispatch({
        type: 'PERFORM_INITIALIZATION',
        processor: setInitialStateProcessor,
        payload: {
          schema: {
            dataGridState: { status: FRS_LOADED },
            statuses: { status: FRS_LOADED }
          },
          statuses: {
            source: [...reimbursementStatuses],
            value: [{ id: RBS_PENDING, name: 'Submitted - Pending' }]
          },
          ssu: {
            studySites: [],
            studies: [],
            sites: [],
            selectedStudyMap: {},
            selectedSiteMap: {},
            selectedStudies: [],
            selectedSites: []
          },
          assigned: {
            source: [{ id: null, name: 'Empty' }],
            value: null
          },
          dataGridState: {
            columns: {
              columnVisibilityModel: {
                mrn: false,
                processingTime: false,
                sponsor: false,
                siteNumber: false,
                reimbursementType: false
              }
            }
          },
          cacheCollector
        }
      });
    },
    [dispatch, status]
  );

  useEffect(
    function() {
      if (isEmpty(gridData)) return;
      const allAssignees = gridData.map(function({ assigneePersonnelId, assigneePersonnelName }) {
        return { id: assigneePersonnelId, name: assigneePersonnelName };
      });

      dispatch({
        type: 'PERFORM',
        processor: setAssignedProcessor,
        payload: allAssignees
      });
    },
    [dispatch, gridData]
  );

  if (isEmpty(schema)) return null;

  return (
    <PageInfoHeader>
      <div className="general-header-wrapper" style={{ minHeight: 71 }}>
        <BasicMultiselect uniqKey="statuses" state={state} dispatch={dispatch} label="Status" />
        <StudySiteMultiselect uniqKey="ssu" state={state} dispatch={dispatch} getStudySites={getStudySites} />
        <Assigned uniqKey="assigned" state={state} dispatch={dispatch} />
        {status === FRS_READY ? (
          <ApplyAndResetButtons
            applyClassName="apply-btn"
            onApply={function() {
              applyButton();
            }}
            applyDisabled={!loadingAllowed}
          />
        ) : (
          <Loader dark={true} />
        )}
      </div>
    </PageInfoHeader>
  );
}

function cacheCollector(state) {
  return {
    statuses: {
      value: state?.statuses?.value
    },
    ssu: {
      selectedStudyMap: state?.ssu?.selectedStudyMap,
      selectedSiteMap: state?.ssu?.selectedSiteMap
    },
    assigned: {
      value: state?.assigned?.value
    },
    dataGridState: state.dataGridState
  };
}

function setAssignedProcessor(state, action) {
  const { type, payload } = action;

  const notExistInFilters = differenceBy(payload, state.assigned?.source, 'id');

  if (isEmpty(notExistInFilters)) {
    return state;
  }
  return {
    ...state,
    assigned: {
      ...state.assigned,
      source: [...state.assigned.source, ...notExistInFilters],
      value: [...state.assigned.value, ...notExistInFilters]
    }
  };
}
